import React from 'react';
import Col from 'react-bootstrap/Col';
import Collapse from 'react-bootstrap/Collapse';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import { useTouchableDropdown } from '../../hooks/useTouchableDropdown';
import { ImageModel, LinkModel } from '../../models/globals';
import { Responsive } from '../common/Responsive';
import { SearchCourses } from '../common/SearchCourses';
import { AccountAndSearch } from './AccountAndSearch';
import { ArrowToggle } from './ArrowToggle';
import { SubMenu } from './SubMenu';
import { RtcBanner } from './RtcBanner';

interface HeaderProps {
    model: HeaderModel;
}

interface HeaderModel {
    categoryMenuModels: CategoryMenuModel[];
    ed2GoResourceLinks: LinkModel[];
    accountLinks: LinkModel[];
    studentLoginLink: LinkModel;
    showStudentLoginLink: boolean;
    contactPhoneNumber: string;
    contactPhoneNumberLink: string;
    logo: ImageModel;
    rtcBannerCartIcon: ImageModel;
    rtcBannerArrowIcon: ImageModel;
    rtcBannerCloseIcon: ImageModel;
    isRtcBannerFeatureFlagEnabled: boolean;
    homeLink: LinkModel;
    showEnvironmentTag: boolean;
    environmentName: string;
    searchLink: LinkModel;
    caaLink: LinkModel;
    autocompleteApiPath: string;
    searchPath: string;
    shoppingCartCount: number;
    shoppingCartLink: LinkModel;
    scopedCssUrl: string;
    typeAheadCssUrl: string;
}

interface CategoryMenuModel {
    category: LinkModel;
    subCategories: LinkModel[];
}

const Header: React.FC<HeaderProps> = ({
    model: {
        categoryMenuModels,
        ed2GoResourceLinks,
        accountLinks,
        studentLoginLink,
        showStudentLoginLink,
        contactPhoneNumber,
        contactPhoneNumberLink,
        logo,
        rtcBannerCartIcon,
        rtcBannerArrowIcon,
        rtcBannerCloseIcon,
        isRtcBannerFeatureFlagEnabled,
        homeLink,
        showEnvironmentTag,
        environmentName,
        searchLink,
        caaLink,
        autocompleteApiPath,
        searchPath,
        shoppingCartCount,
        shoppingCartLink,
    },
}) => {
    const [showSearch, setShowSearch] = React.useState(false);
    const toggleShowSearch = () => setShowSearch((show) => !show);
    const { dropdown: coursesDropdown } = useTouchableDropdown();
    const { dropdown: resourcesDropdown } = useTouchableDropdown();

    return (
        <>
            <header>
                {showEnvironmentTag && (
                    <div className="environment bg-info rounded p-0 font-weight-bold">{environmentName}</div>
                )}

                <RtcBanner
                    rtcBannerCartIcon={rtcBannerCartIcon}
                    rtcBannerArrowIcon={rtcBannerArrowIcon}
                    rtcBannerCloseIcon={rtcBannerCloseIcon}
                    isRtcBannerFeatureFlagEnabled={isRtcBannerFeatureFlagEnabled}
                    shoppingCartCount={shoppingCartCount}
                    shoppingCartLink={shoppingCartLink}
                />

                <Navbar aria-label="header" className="border-bottom shadow-sm" expand="md">
                    <Container>
                        <Navbar.Toggle aria-controls="responsive-navbar-nav" className="mr-3" />

                        <Navbar.Brand className="mr-auto nav-logo" href={homeLink.url}>
                            <img alt={logo.altText} className="w-100" src={logo.url} />
                        </Navbar.Brand>

                        <Responsive className="row" testIdSuffix="account-and-search" viewMode="mobile">
                            <AccountAndSearch
                                accountLinks={accountLinks}
                                showSearch={showSearch}
                                showStudentLoginLink={showStudentLoginLink}
                                studentLoginLink={studentLoginLink}
                                toggleShowSearch={toggleShowSearch}
                                shoppingCartCount={shoppingCartCount}
                                shoppingCartLink={shoppingCartLink}
                            />
                        </Responsive>

                        <Navbar.Collapse id="responsive-navbar-nav">
                            <Nav className="mr-auto zindex-header-menu">
                                <Dropdown
                                    {...coursesDropdown}
                                    as={Nav.Item}
                                    className="pl-3 navitem-border-bottom"
                                    focusFirstItemOnShow="keyboard"
                                    role="toolbar" // tells NVDA to enable navigation by arrow keys
                                >
                                    <Dropdown.Toggle as={ArrowToggle} id="courses-nav-dropdown">
                                        Courses
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu renderOnMount>
                                        <Dropdown.Item active={false} href={searchLink.url}>
                                            <span className="menu-border-bottom">{searchLink.name}</span>
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        <Dropdown.ItemText>
                                            {categoryMenuModels.map(({ category, subCategories }) => (
                                                <SubMenu
                                                    category={category}
                                                    subCategories={subCategories}
                                                    key={`${category.name}-submenu`}
                                                />
                                            ))}
                                        </Dropdown.ItemText>
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Dropdown
                                    {...resourcesDropdown}
                                    as={Nav.Item}
                                    className="pl-3 navitem-border-bottom"
                                    focusFirstItemOnShow="keyboard"
                                >
                                    <Dropdown.Toggle as={ArrowToggle} id="resources-nav-dropdown">
                                        Resources
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu renderOnMount>
                                        {ed2GoResourceLinks.map(({ name, url }) => (
                                            <Dropdown.Item active={false} href={url} key={name}>
                                                <span className="menu-border-bottom">{name}</span>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Nav.Link className="pl-3" href={caaLink.url}>
                                    {caaLink.name}
                                </Nav.Link>
                            </Nav>
                        </Navbar.Collapse>

                        <Responsive className="row" testIdSuffix="account-and-search" viewMode="desktop-and-tablet">
                            <AccountAndSearch
                                accountLinks={accountLinks}
                                showSearch={showSearch}
                                showStudentLoginLink={showStudentLoginLink}
                                studentLoginLink={studentLoginLink}
                                toggleShowSearch={toggleShowSearch}
                                shoppingCartCount={shoppingCartCount}
                                shoppingCartLink={shoppingCartLink}
                            />
                        </Responsive>

                        <Nav className="border-left d-none d-md-block ml-3 pl-2 text-center">
                            <Navbar.Text>
                                <div>CONTACT US</div>
                                <Nav.Link className="py-0" href={contactPhoneNumberLink}>
                                    {contactPhoneNumber}
                                </Nav.Link>
                            </Navbar.Text>
                        </Nav>
                    </Container>
                </Navbar>
            </header>

            <Collapse in={showSearch}>
                <div className="border-bottom shadow-sm">
                    <Container>
                        <Row className="justify-content-center py-3">
                            <Col md="8" lg="6">
                                <SearchCourses
                                    autocompleteApiPath={autocompleteApiPath}
                                    className="py-3"
                                    id="header-search-courses"
                                    placeholder="Search courses"
                                    searchPath={searchPath}
                                />
                            </Col>
                        </Row>
                    </Container>
                </div>
            </Collapse>
        </>
    );
};

export { Header, HeaderProps, HeaderModel };
